import { useState } from 'react'
import type { ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useAdminWebhookChannels } from '@/api/queries'
import { Button, Modal, Select, Spinner, TextField, useToast } from '@/components/ui'
import { AdminPage, Card, ErrorNotice, useCopy } from './AdminLayout'
import styles from './ApiKeysPage.module.css'

interface IntegrationKey {
  id: string; name: string; secret_prefix: string; scopes: string[]; room_ids: string[]
  expires_at: string; last_used_at: string | null; revoked_at: string | null
}
const keyQuery = ['admin', 'integration-keys'] as const
const permissions = [
  ['messages:write', 'Post messages'], ['widgets:read', 'Read widgets'], ['widgets:write', 'Update widgets'],
] as const

export function ApiKeysPage(): ReactElement {
  const queryClient = useQueryClient()
  const keys = useQuery({ queryKey: keyQuery, queryFn: ({ signal }) => api.get<{ keys: IntegrationKey[] }>('/admin/api-keys', undefined, signal) })
  const channels = useAdminWebhookChannels()
  const [name, setName] = useState('')
  const [days, setDays] = useState('90')
  const [rooms, setRooms] = useState<string[]>([])
  const [scopes, setScopes] = useState<string[]>(['widgets:write'])
  const [secret, setSecret] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<IntegrationKey | null>(null)
  const copy = useCopy()
  const toast = useToast()
  const create = useMutation({
    mutationFn: () => api.post<{ secret: string }>('/admin/api-keys', {
      name, scopes, room_ids: rooms, expires_at: new Date(Date.now() + Number(days) * 86_400_000).toISOString(),
    }),
    onSuccess: (issued) => { setSecret(issued.secret); setName(''); void queryClient.invalidateQueries({ queryKey: keyQuery }) },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/admin/api-keys/${encodeURIComponent(id)}`),
    onSuccess: () => { setRevoking(null); toast.success('API key revoked.'); void queryClient.invalidateQueries({ queryKey: keyQuery }) },
  })
  const toggle = (values: string[], value: string) => values.includes(value) ? values.filter((item) => item !== value) : [...values, value]

  return <AdminPage title="API keys" lead="Give external systems access to selected channels and actions.">
    <Card title="Create a key" lead="The secret is shown once. Store it in the external system that will use it.">
      <form className={styles.form} onSubmit={(event) => { event.preventDefault(); create.mutate() }}>
        <TextField label="Name" value={name} maxLength={80} required onChange={(event) => setName(event.target.value)} placeholder="Operations dashboard" />
        <Select label="Expires after" value={days} onChange={(event) => setDays(event.target.value)} options={['7', '30', '90', '365'].map((value) => ({ value, label: `${value} days` }))} />
        <fieldset className={styles.choices}><legend>Permissions</legend>{permissions.map(([scope, label]) => <label key={scope}>
          <input type="checkbox" checked={scopes.includes(scope)} onChange={() => setScopes(toggle(scopes, scope))} /> {label}
        </label>)}</fieldset>
        <fieldset className={styles.choices}><legend>Channels</legend>
          {channels.isPending ? <Spinner label="Loading channels" /> : channels.error ? <ErrorNotice error={channels.error} /> : channels.data?.length === 0 ? <p>Create a local channel first.</p> : channels.data?.map((room) => <label key={room.id}>
            <input type="checkbox" checked={rooms.includes(room.id)} onChange={() => setRooms(toggle(rooms, room.id))} /> #{room.name ?? room.slug}
          </label>)}
        </fieldset>
        {create.error ? <ErrorNotice error={create.error} title="The key could not be created" /> : null}
        <Button type="submit" variant="primary" loading={create.isPending} disabled={!name.trim() || rooms.length === 0 || scopes.length === 0}>Create API key</Button>
      </form>
    </Card>
    <Card title="Issued keys">
      {keys.isPending ? <Spinner label="Loading API keys" /> : keys.error ? <ErrorNotice error={keys.error} /> : keys.data?.keys.length === 0 ? <p>No API keys have been issued.</p> : <ul className={styles.keys}>
        {keys.data?.keys.map((key) => {
          const expired = Date.parse(key.expires_at) <= Date.now()
          return <li key={key.id}>
            <div><strong>{key.name}</strong><p><code>{key.secret_prefix}…</code> · {key.revoked_at ? 'Revoked' : expired ? 'Expired' : `Expires ${new Date(key.expires_at).toLocaleDateString()}`}</p>
              <p>{key.scopes.map((scope) => permissions.find(([value]) => value === scope)?.[1] ?? scope).join(' · ')}</p>
              <p>{key.room_ids.map((id) => { const room = channels.data?.find((channel) => channel.id === id); return room ? `#${room.name ?? room.slug}` : 'Removed channel' }).join(', ')}</p>
              <p>{key.last_used_at ? `Last used ${new Date(key.last_used_at).toLocaleString()}` : 'Not used yet'}</p>
            </div>
            {!key.revoked_at ? <Button variant="danger" size="sm" onClick={() => { revoke.reset(); setRevoking(key) }}>Revoke</Button> : null}
          </li>
        })}
      </ul>}
    </Card>
    <Modal open={secret !== null} onClose={() => setSecret(null)} title="Copy your API key" size="sm">
      <div className={styles.form}><p>This secret cannot be shown again. Copy it before closing this window.</p>
        <TextField label="API key" value={secret ?? ''} readOnly spellCheck={false} autoComplete="off" />
        <Button variant="primary" onClick={() => secret && copy(secret, 'API key')}>Copy key</Button>
        <Button onClick={() => setSecret(null)}>Done</Button>
      </div>
    </Modal>
    <Modal open={revoking !== null} onClose={() => !revoke.isPending && setRevoking(null)} title="Revoke API key" size="sm">
      <div className={styles.form}><p>{revoking?.name} will immediately lose access. Past messages and widgets will remain.</p>
        {revoke.error ? <ErrorNotice error={revoke.error} /> : null}
        <Button variant="danger" loading={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>Revoke key</Button>
      </div>
    </Modal>
  </AdminPage>
}
